import type { AxiosRequestConfig } from 'axios';
import { setAuthorizationHeader } from '@auth/utils/headerUtils';
import { getCheckoutTokenFromCookies } from '@auth/utils/tokenUtils';

export type GetAccessTokenFn = () => Promise<string | null>;

/**
 * The single source-of-truth Axios request interceptor for attaching auth
 * credentials, following the three-tier fallback strategy:
 *
 *  1. **Auth0 access token** (Bearer header) — preferred for authenticated users.
 *  2. **Checkout access token** (Bearer header) — fallback for guest checkout
 *     flows where the user has a JWT issued by the checkout service but no Auth0
 *     session.
 *  3. **withCredentials** — no explicit header; the browser sends session
 *     cookies automatically when the Axios instance has `withCredentials: true`.
 *
 * A failure to resolve the Auth0 token (e.g. timeout, unauthenticated) is
 * swallowed so the request still proceeds — falling through to the lower
 * priority strategies rather than blocking the request.
 */
export const createAuthRequestInterceptor =
  (getAccessToken: GetAccessTokenFn) =>
  async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    // --- Priority 1: Auth0 access token ---
    let auth0Token: string | null = null;
    try {
      auth0Token = await getAccessToken();
    } catch {
      // Auth0 unavailable (e.g. timeout / unauthenticated) — fall through to
      // lower-priority strategies.
    }
    if (auth0Token) {
      setAuthorizationHeader(config, auth0Token);
      return config;
    }

    // --- Priority 2: Checkout access token (cookie-based JWT) ---
    const checkoutToken = getCheckoutTokenFromCookies();
    if (checkoutToken) {
      setAuthorizationHeader(config, checkoutToken);
      return config;
    }

    // --- Priority 3: withCredentials (set on the Axios instance) ---
    // No explicit header needed — the browser sends session cookies automatically.
    return config;
  };
